/* eslint-disable max-lines */
import {
  Box,
  BoxType,
  Camera,
  Escalation,
  EscalationState,
  Location,
  Point,
  Polygon,
  StatusType,
  UnifiedAlarm,
  UnifiedAlarmGroup,
} from '@hakimo-ui/hakimo/types';
import {
  CameraZoneField,
  SpecialZoneType,
  ZonePolygonData,
} from '@hakimo-ui/hakimo/ui-elements';
import { LabelType } from '@hakimo-ui/shared/ui-base';
import dayjs from 'dayjs';
import dayjsTimezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { MIN_CAMERA_BOX_WIDTH, ZONE_OVERLAY_OPTIONS } from './constants';
import {
  CameraDetails,
  CameraFeedMode,
  EnterpriseAlarmDetails,
  Position,
  SectionDimension,
} from './types';

dayjs.extend(utc);
dayjs.extend(dayjsTimezone);

export function mapStatusTypeToLabelType(statusType: StatusType): LabelType {
  switch (statusType) {
    case StatusType.RESOLVED:
      return 'success';
    case StatusType.IN_PROGRESS:
      return 'warning';
    case StatusType.PENDING:
      return 'error';
  }
}

export function mapStatusToStatusType(status: string): StatusType {
  switch (status.toLowerCase()) {
    case 'resolved by hakimo':
    case 'resolved manually':
    case 'alarm acknowledged via acs':
    case 'resolved':
    case 'resolved_by_operator':
    case 'resolved_by_hakimo':
      return StatusType.RESOLVED;
    case 'analyzing':
    case 'pending':
    case 'pending_cutoff':
      return StatusType.PENDING;
    case 'in progress':
    case 'in_progress':
      return StatusType.IN_PROGRESS;
    default:
      return StatusType.PENDING;
  }
}

export const removeDuplicateAlarms = (
  alarmGroup: UnifiedAlarmGroup
): UnifiedAlarmGroup => {
  const updatedAlarmGroup = { ...alarmGroup };
  const uniqueAlarms = new Map<string, UnifiedAlarm>();

  alarmGroup.alarms.forEach((alarm) => {
    const key = alarm.id;
    if (!uniqueAlarms.has(key)) {
      uniqueAlarms.set(key, alarm);
    }
  });

  updatedAlarmGroup.alarms = Array.from(uniqueAlarms.values());

  return updatedAlarmGroup;
};

const hasFaceMatch = (alarm: UnifiedAlarm): boolean =>
  alarm.media.frames?.some((f) => (f.matched_faces?.length ?? 0) > 0) ?? false;

// Collapses scan alarms that target the same camera and time window into a
// single record. The backend can emit a base-scan and a face-scan alarm for one
// event; without this, render order between them is non-deterministic.
// Critical (unresolved) wins over Minor (resolved), as in the location dedupe:
// v3 scan-shaped groups carry resolved minors (ai-engine #8071), and a resolved
// copy must not hide the alarm still waiting on an operator. Within the same
// resolution state, the one whose frames contain face matches wins. v2 scan
// always sends `resolvedAlarm: false`, so it only ever compares face matches.
export const dedupeScanAlarmsByCameraAndTime = (
  alarms: UnifiedAlarm[]
): UnifiedAlarm[] => {
  const picked = new Map<string, UnifiedAlarm>();
  const order: string[] = [];

  const isHigherPriority = (
    candidate: UnifiedAlarm,
    current: UnifiedAlarm
  ): boolean => {
    if (candidate.resolvedAlarm !== current.resolvedAlarm) {
      return !candidate.resolvedAlarm;
    }
    return !hasFaceMatch(current) && hasFaceMatch(candidate);
  };

  for (const alarm of alarms) {
    // Sources differ (socket payload, location-alarm transform, neighbour
    // fetch) and a single bad entry must not blank the whole group: the
    // show-minor-alarms path hands the array over unfiltered.
    if (!alarm) {
      continue;
    }
    const key = `${alarm.cameraId}|${alarm.alarmStartTime}|${alarm.alarmEndTime}`;
    const existing = picked.get(key);
    if (!existing) {
      picked.set(key, alarm);
      order.push(key);
      continue;
    }
    if (isHigherPriority(alarm, existing)) {
      picked.set(key, alarm);
    }
  }

  return order.map((key) => picked.get(key) as UnifiedAlarm);
};

// Collapses location alarms that target the same camera and time window into
// a single record, preferring the higher-priority one. The backend can emit
// multiple raw alarms for one motion event (e.g. one auto-resolved by Hakimo,
// one still pending); without this, render order between them is
// non-deterministic. Critical (unresolved) wins over Minor (resolved); tap
// breaks ties within the same resolution state.
export const dedupeLocationAlarmsByCameraAndTime = (
  alarms: UnifiedAlarm[]
): UnifiedAlarm[] => {
  const picked = new Map<string, UnifiedAlarm>();
  const order: string[] = [];

  const isHigherPriority = (
    candidate: UnifiedAlarm,
    current: UnifiedAlarm
  ): boolean => {
    if (candidate.resolvedAlarm !== current.resolvedAlarm) {
      return !candidate.resolvedAlarm;
    }
    return (candidate.tap ?? -Infinity) > (current.tap ?? -Infinity);
  };

  for (const alarm of alarms) {
    // Sources differ (socket payload, location-alarm transform, neighbour
    // fetch) and a single bad entry must not blank the whole group: the
    // show-minor-alarms path hands the array over unfiltered.
    if (!alarm) {
      continue;
    }
    const key = `${alarm.cameraId}|${alarm.alarmStartTime}|${alarm.alarmEndTime}`;
    const existing = picked.get(key);
    if (!existing) {
      picked.set(key, alarm);
      order.push(key);
      continue;
    }
    if (isHigherPriority(alarm, existing)) {
      picked.set(key, alarm);
    }
  }

  return order.map((key) => picked.get(key) as UnifiedAlarm);
};

export const getDetailsMapping = (
  alarms: UnifiedAlarm[]
): {
  camToDetailsMap: Record<string, CameraDetails>;
  alarmToDetailsMap: Record<string, UnifiedAlarm>;
} => {
  const camToDetailsMap: Record<string, CameraDetails> = {};
  const alarmToDetailsMap: Record<string, UnifiedAlarm> = {};

  alarms.forEach((alarm) => {
    const camId = alarm.cameraId;
    if (!camToDetailsMap[camId]) {
      camToDetailsMap[camId] = {
        cameraId: camId,
        cameraName: alarm.cameraName,
        cameraGroupId: alarm.cameraGroupId,
        cameraGroupName: alarm.cameraGroupName,
        cameraLiveStreamUrl: alarm.cameraLiveStreamUrl,
        cameraPlayBackUrl: alarm.cameraPlayBackUrl,
        alarms: [],
        isTalkdownEnabled: alarm.isTalkDownEnabled,
      };
    }
    if (!camToDetailsMap[camId].alarmStartAndEndTimes) {
      camToDetailsMap[camId].alarmStartAndEndTimes = [];
    }
    camToDetailsMap[camId].alarmStartAndEndTimes.push([
      alarm.alarmStartTime,
      alarm.alarmEndTime,
    ]);

    camToDetailsMap[camId].alarms.push(alarm);

    alarmToDetailsMap[alarm.id] = alarm;
  });
  return { camToDetailsMap, alarmToDetailsMap };
};

export function getCameraZonePolygons(
  camera: Camera | undefined,
  visibleZoneTypes: Set<SpecialZoneType>
): ZonePolygonData[] {
  if (!camera || visibleZoneTypes.size === 0) {
    return [];
  }

  const cameraZoneData: Pick<Camera, CameraZoneField> = camera;

  return ZONE_OVERLAY_OPTIONS.flatMap(({ id, cameraField }) => {
    if (!visibleZoneTypes.has(id)) {
      return [];
    }

    return (cameraZoneData[cameraField] ?? []).map((points, index) => ({
      id: `${camera.id}-${id}-${index}`,
      type: id,
      points: points.map(([x, y]) => ({ x, y })),
    }));
  });
}

export const getCameraPositionsMap = (
  globalCamMode: CameraFeedMode,
  sectionDimensions: SectionDimension,
  allCameras: string[],
  rowCount: number,
  colCount: number
) => {
  if (!sectionDimensions.width || !sectionDimensions.height) return {};

  const gapSize = 2; // Minimum gap between boxes

  const availableWidth = sectionDimensions.width - gapSize * (colCount + 1);
  const availableHeight = sectionDimensions.height - gapSize * (rowCount + 1);

  let boxWidth = availableWidth / colCount;

  boxWidth = Math.max(MIN_CAMERA_BOX_WIDTH, boxWidth);

  // Apply 16:9 aspect ratio to calculate height
  let boxHeight = boxWidth * (9 / 16);
  if (globalCamMode === CameraFeedMode.SPLIT) {
    boxHeight = boxHeight / 2;
  }

  // optimise for less number of cameras to get it accomodated in the section itself
  if (
    boxWidth > MIN_CAMERA_BOX_WIDTH &&
    boxHeight * rowCount > availableHeight &&
    globalCamMode !== CameraFeedMode.SPLIT
  ) {
    const newBoxHeight = availableHeight / rowCount;
    const newBoxWidth = newBoxHeight * (16 / 9);
    if (newBoxWidth > MIN_CAMERA_BOX_WIDTH) {
      boxHeight = newBoxHeight;
      boxWidth = newBoxWidth;
    }
  }

  // Calculate positions for each camera
  const positionMap: Record<string, Position> = {};

  // Center the boxes horizontally within available width if there's extra space
  const totalBoxesWidth = boxWidth * colCount + gapSize * (colCount - 1);
  const horizontalOffset = Math.max(
    0,
    (sectionDimensions.width - totalBoxesWidth) / 2
  );

  allCameras.forEach((camId, i) => {
    const row = Math.floor(i / colCount);
    const col = i % colCount;

    // Calculate position with gaps and centered horizontally
    const left = horizontalOffset + col * (boxWidth + gapSize);
    const top = row * (boxHeight + gapSize) + gapSize;
    positionMap[camId] = {
      top: Math.floor(top),
      left: Math.floor(left),
      width: Math.floor(boxWidth),
      height: Math.floor(boxHeight),
    };
  });

  return positionMap;
};

export const getRowAndColCount = (
  globalCamMode: CameraFeedMode,
  sectionDimensions: SectionDimension,
  totalCams: number
) => {
  if (globalCamMode === CameraFeedMode.SPLIT) {
    return [totalCams, 1];
  }

  const gapSize = 2; // Same value as in cameraPositions calculation

  // Calculate max columns that can fit in the container width without horizontal scroll
  const maxPossibleCols =
    sectionDimensions.width > 0
      ? Math.floor(
          (sectionDimensions.width - gapSize) / (MIN_CAMERA_BOX_WIDTH + gapSize)
        )
      : Math.ceil(Math.sqrt(totalCams));

  if (totalCams > 2 && totalCams <= 4 && maxPossibleCols > 2) {
    return [2, 2];
  }

  // Ensure at least 1 column
  const cols = Math.max(1, Math.min(maxPossibleCols, totalCams));

  // Calculate rows based on columns
  const rows = Math.ceil(totalCams / cols);

  return [rows, cols];
};

export const hasMedia = (alarm: UnifiedAlarm): boolean =>
  !!alarm.media?.video?.url || (alarm.media?.frames?.length ?? 0) > 0;

export const groupHasMedia = (alarms: UnifiedAlarm[]): boolean =>
  alarms.some(hasMedia);

// Vision sent the door's name, not the door, before vision#663. Read both, so
// the card keeps naming the door whichever side is deployed first.
function doorName(door: UnifiedAlarm['door'] | string): string | undefined {
  return typeof door === 'string' ? door : door?.name;
}

export function getEnterpriseAlarmDetails(
  alarm: UnifiedAlarm,
  overrides: {
    tapScore?: number;
    timestamp?: string;
    locationAlarmType?: string;
  } = {}
): EnterpriseAlarmDetails {
  return {
    tap: overrides.tapScore ?? alarm.tap,
    alarmTime: overrides.timestamp,
    alarmType: overrides.locationAlarmType,
    employee: alarm.employee,
    alarmSourceSystem: alarm.alarm_source_system,
    sourceEntity: doorName(alarm.door),
    camera: alarm.cameraName,
  };
}

export function getEnterpriseFieldRows(
  details: EnterpriseAlarmDetails,
  options: { location?: string } = {}
): { label: string; value: string | number }[] {
  const rows: { label: string; value: string | number | undefined }[] = [
    { label: 'True Alarm Probability', value: details.tap },
    { label: 'Alarm Type', value: details.alarmType },
    {
      label: 'Alarm Time',
      value: details.alarmTime
        ? new Date(details.alarmTime).toLocaleString()
        : undefined,
    },
    { label: 'Source Entity', value: details.sourceEntity },
    { label: 'Employee', value: details.employee },
    { label: 'Location', value: options.location },
    { label: 'Alarm Source System', value: details.alarmSourceSystem },
    { label: 'Camera', value: details.camera },
  ];
  return rows.filter(
    (r): r is { label: string; value: string | number } =>
      r.value !== undefined && r.value !== ''
  );
}

/**
 * One line describing an enterprise alarm, for the card that stands in for the
 * AI recommendation. Undefined when there is nothing worth showing, which is
 * the card's signal to render nothing at all.
 *
 * Times are formatted in the location's zone, not the browser's: the card
 * names that zone in its footer and reads the clicked HH:mm:ss back in it to
 * seek, so a browser-local string would both mislabel the event and seek to
 * the wrong moment. An empty zone falls back to UTC -- dayjs throws a
 * RangeError on `''`, which would take the whole SOP panel down.
 */
export function formatEnterpriseAlarmSummary(
  details: EnterpriseAlarmDetails,
  timeZone: string
): string | undefined {
  const { alarmType, alarmTime, camera, sourceEntity, employee } = details;
  const zone = timeZone || 'UTC';

  const when = alarmTime
    ? dayjs(alarmTime).tz(zone).format('YYYY-MM-DD HH:mm:ss')
    : undefined;

  if (!alarmType && !when && !sourceEntity && !camera && !employee) {
    return undefined;
  }

  // The door and the camera are different things -- one door may have several
  // cameras or none -- so each is named rather than collapsed into one slot.
  // They can carry the same name though, and "on X (X)" reads like a mistake.
  const event = [
    alarmType || 'Alarm',
    when && `at ${when}`,
    sourceEntity && `on ${sourceEntity}`,
    camera && camera !== sourceEntity && `(${camera})`,
  ]
    .filter(Boolean)
    .join(' ');

  // Who badged is the operative fact on a tailgate or badge mismatch, so it
  // rides along rather than sending the operator elsewhere for it.
  return employee ? `${event}, badge ${employee}` : event;
}

export const getStartAndEndTime = (alarms: UnifiedAlarm[]) => {
  const alarmTimes = alarms.reduce((acc, item) => {
    acc.push(item.alarmStartTime);
    acc.push(item.alarmEndTime);
    return acc;
  }, [] as number[]);
  if (alarmTimes.length === 0) {
    return [0, 0];
  }
  alarmTimes.sort();
  return [alarmTimes[0], alarmTimes[alarmTimes.length - 1]];
};

function getTagTextFromBoxType(type?: BoxType) {
  switch (type) {
    case BoxType.PERSON:
      return 'Person';
    case BoxType.LOITERING_PERSON:
      return 'Loitering Person';
    case BoxType.PERSON_WITH_WEAPON:
      return 'Weapon';
    case BoxType.VEHICLE:
      return 'Vehicle';
    default:
      return '';
  }
}

function getColorFromBoxType(type?: BoxType): string {
  switch (type) {
    case BoxType.VEHICLE:
      return 'cyan';
    case BoxType.PERSON_WITH_WEAPON:
      return 'red';
    default:
      return 'yellow';
  }
}

export const getVideoPolygons = (boxes: Box[]): Polygon[] => {
  return boxes.map((box) => {
    const [left, top, width, height] = box.coords;
    const points: Point[] = [
      [left, top],
      [left + width, top],
      [left + width, top + height],
      [left, top + height],
    ];
    const polygon: Polygon = {
      color: getColorFromBoxType(box.type),
      pointSize: 'small',
      closed: true,
      points,
      tag: getTagTextFromBoxType(box.type),
    };
    return polygon;
  });
};

export const normalizeAlarmGroupLocation = (
  location: UnifiedAlarmGroup['location'] | Location,
  tenantId?: string
): Location => {
  if (typeof location.id === 'number') {
    return location as Location;
  }
  const fallbackName = location.name || tenantId;

  return {
    id: parseInt(location.id) || 0,
    name: fallbackName || '',
    tenant_id: location.tenant_id,
    city: location.city || '',
    country: location.country || '',
    description: location.description || '',
    state: location.state || '',
    timezone: location.timezone || 'UTC',
  };
};

export function normalizeAlarmGroupStatus(status: string): string {
  switch (status.toLowerCase()) {
    case 'pending':
    case 'pending_cutoff':
      return 'Pending';
    case 'resolved_by_operator':
    case 'resolved_by_hakimo':
    case 'resolved':
      return 'Resolved';
    // The same status arrives in two forms depending on which service produced
    // it, not on the data version: scan/event-flow sends the wire form
    // (`in_progress`), while ORM location alarms send the display form
    // (`In Progress`) on both v2 and v3. Matching only the wire form let every
    // ORM-sourced status fall through to the default, which is why "In Progress"
    // read grey there and yellow on scan.
    //
    // Listed explicitly rather than normalising the separator globally: doing
    // that would also fold the space-separated raw alarm statuses, silently
    // turning "Resolved by Hakimo" green and rewriting its text to "Resolved".
    case 'in_progress':
    case 'in progress':
      return 'In Progress';
    default:
      return status;
  }
}

export function mapAlarmGroupStatusToLabelType(status: string): LabelType {
  switch (status.toLowerCase()) {
    case 'resolved':
    case 'resolved_by_operator':
    case 'resolved_by_hakimo':
      return 'success';
    // See normalizeAlarmGroupStatus: both wire forms, nothing else.
    case 'in_progress':
    case 'in progress':
      return 'warning';
    case 'pending':
    case 'pending_cutoff':
      return 'error';
    default:
      return 'default';
  }
}

export function mapAlarmGroupSeverityToLabelType(severity: string): LabelType {
  switch (severity.toLowerCase()) {
    case 'high':
      return 'error';
    case 'low':
      return 'success';
    default:
      return 'info';
  }
}

export function escalationFromState(
  state: EscalationState,
  initiatingLocationAlarmId = ''
): Escalation {
  const openedAt = state.escalationOpenTimestamp;
  const startedAt = Number.isFinite(openedAt)
    ? new Date(openedAt).toISOString()
    : '';

  return {
    id: state.escalationId,
    title: 'Active Escalation',
    description: state.comment,
    incidentStartTimeUtc: startedAt,
    initiatingLocationAlarmId,
  };
}
